/**
 * Product-analytics event contract (measurement wave 1).
 *
 * Source of truth: entregables/alumnia/rediseno-fase2-2026-09-23/medicion-y-digest.md
 * §3.2 (common context) and §3.3 (the ten events). Names and property keys are
 * copied from that document. Every event answers one question from §2; if no
 * one will read it, it does not go in.
 *
 * Rules (enforced by `track()` in ./track.ts and its tests):
 *   - snake_case `object_action` names;
 *   - no free text (search queries, post or ask bodies) and no third-party ids —
 *     only lengths, counts, enums and positions;
 *   - nothing that already exists as a database row (post created, connection
 *     sent, message sent, ask created). Those are counted from Supabase.
 *
 * Which events fire today: `LIVE_EVENTS` below. The rest are defined now so the
 * redesign (R1) wires them against a fixed contract and the before/after
 * comparison uses the same names; see `CONTRACT_ONLY_EVENTS` for why each waits.
 */

// ─── Common context (registered once as PostHog super properties) ──────────

/** Interface generation, as resolved by the UI switch (lib/ui-variant). */
export type { UiVersion } from '@/lib/ui-variant/resolve'
import type { UiVersion } from '@/lib/ui-variant/resolve'

/**
 * The org layout renders `<meta name={UI_VERSION_META} content={version}>` with
 * the server-resolved version (global row or preview cookie, which is
 * httpOnly), so PostHog's init can register it before the first `$pageview`.
 */
export const UI_VERSION_META = 'alumnia-ui-version'

export function uiVersionFromDocument(doc: Pick<Document, 'querySelector'>): UiVersion {
  const content = doc.querySelector(`meta[name="${UI_VERSION_META}"]`)?.getAttribute('content')
  return content === 'encuentro' ? 'encuentro' : 'classic'
}

export type MemberRole = 'member' | 'staff' | 'admin' | 'super_admin'
export type AppPlatform = 'web' | 'ios' | 'android'
export type AppEnv = 'production' | 'staging' | 'development'

/** Super properties attached to every event, `$pageview` included. */
export interface AnalyticsContextProps {
  org_id?: string
  org_slug?: string
  org_is_demo?: boolean
  member_role?: MemberRole
  ui_version: UiVersion
  app_platform: AppPlatform
  app_env: AppEnv
}

/** PostHog group type for the school. `posthog.group(ORG_GROUP_TYPE, org.id, …)`. */
export const ORG_GROUP_TYPE = 'organization'

// ─── Shared enums ───────────────────────────────────────────────────────────

/**
 * Where a navigation lands. The first eight are the R1 destinations from the
 * document; `explore` is the classic "Explorar" tab (groups, events, jobs and
 * mentoring behind one tab), recorded under its own name so the before/after
 * comparison doesn't pretend classic had a Groups item.
 */
export type NavDestination =
  | 'home'
  | 'people'
  | 'groups'
  | 'events'
  | 'jobs'
  | 'messages'
  | 'notifications'
  | 'profile'
  | 'explore'

export type NavSurface = 'sidebar' | 'tabbar' | 'header' | 'cmdk'

/** How a visit started, from `utm_source` on the landing URL. Same values as member_app_visits.source. */
export type EntrySource = 'digest' | 'push' | 'email' | 'direct' | 'other'

/**
 * Search result kinds. `location` and `company` exist only in the classic
 * inline search (they apply a directory filter instead of opening something).
 */
export type SearchResultType =
  | 'person'
  | 'group'
  | 'event'
  | 'job'
  | 'page'
  | 'location'
  | 'company'

/** Search scope: the tab the search ran from (classic) or the palette (R1). */
export type SearchScope = 'people' | 'explore' | 'all'

/** `mentorship_view` = the classic ask composer inside Explore → Mentoring. */
export type AskSurface = 'home' | 'profile' | 'empty_state' | 'cmdk' | 'mentorship_view'

/**
 * The automatic stories that exist (decision of 23/09/2026: only the ones that
 * need no new data; `StoryFeedItem` in lib/community-digest/types.ts). Job
 * changes and anniversaries join when their data exists.
 */
export type StoryType = 'new_classmates' | 'new_jobs' | 'people_at_event'
/** `request`, `message` and `person` are the rows of Para vos and of the filler. */
export type FeedItemType = 'post' | 'story' | 'ask' | 'event' | 'request' | 'message' | 'person'
export type FeedItemAction =
  | 'accept'
  | 'ignore'
  | 'like'
  | 'comment'
  | 'congratulate'
  | 'message'
  | 'connect'
  | 'rsvp'
  | 'help'
  | 'open'
export type FeedSection = 'for_you' | 'feed' | 'fill'
export type ProfilePrompt = 'linkedin_import' | 'photo' | 'experience'

// ─── The ten events ─────────────────────────────────────────────────────────

export interface AnalyticsEventProps {
  /** ¿Qué destinos se usan y desde dónde? */
  nav_clicked: {
    destination: NavDestination
    surface: NavSurface
    /** Route template, never a raw path (see `routeTemplate`). */
    from_route: string
  }
  /** ¿Qué encuentra la gente al llegar a la Home? (R1) */
  home_viewed: {
    for_you_count: number
    feed_count: number
    story_counts: Record<StoryType, number>
    window_days: number
    entry: EntrySource
  }
  /** % de visitas con acción y tasa de acción por tipo de historia. (R1) */
  feed_item_action: {
    item_type: FeedItemType
    story_type?: StoryType
    action: FeedItemAction
    position: number
    section: FeedSection
  }
  /** ¿Se usa la búsqueda / ⌘K? */
  search_opened: {
    trigger: 'shortcut' | 'button'
  }
  /** ¿Encuentra lo que busca? */
  search_result_selected: {
    result_type: SearchResultType
    position: number
    query_len: number
  }
  /** ¿Qué falta? Sólo el largo, nunca el texto. */
  search_no_results: {
    query_len: number
    scope: SearchScope
  }
  /** ¿El mapa reemplaza a Viajes? */
  map_opened: {
    surface: 'people' | 'profile' | 'explore'
    has_city_filter: boolean
  }
  /** Búsqueda por ciudad del mapa y de Personas. (R1) */
  map_city_searched: {
    results_count: number
  }
  /** El embudo de pedidos; la creación ya está en mentorship_asks. */
  ask_started: {
    surface: AskSurface
  }
  /** ¿Funcionan los empujones del perfil? (R1) */
  profile_prompt_clicked: {
    prompt: ProfilePrompt
  }
}

export type AnalyticsEventName = keyof AnalyticsEventProps

/** Wired in the UI (see the call sites of `track`). */
export const LIVE_EVENTS = [
  'nav_clicked',
  'search_opened',
  'search_result_selected',
  'search_no_results',
  'map_opened',
  'ask_started',
  // The Encuentro Home (S1b): components/encuentro/screens/home.
  'home_viewed',
  'feed_item_action',
  'profile_prompt_clicked',
  // Personas (S3a): choosing a city in "Buscar una ciudad", with how many live there.
  'map_city_searched',
] as const satisfies readonly AnalyticsEventName[]

/** Defined for R1; each needs a screen the classic UI doesn't have. */
export const CONTRACT_ONLY_EVENTS = {} as const satisfies Record<
  Exclude<AnalyticsEventName, (typeof LIVE_EVENTS)[number]>,
  string
>
